'use client';

/**
 * BookingWizardSidebarSteps — shell sidebar MULTI-STEP del BookingWizard unico
 * (Fase 1: noleggio). Drop-in di BookingSidebar: STESSE props e STESSO callback
 * onBookingRequest → BoatDetailView mantiene la logica di submit/pagamento.
 * Cambia solo la UI (step: Date → Ospiti → Extra → Riepilogo → Pagamento).
 *
 * Riusa useBookingState (single source of truth) e i totali "effective".
 */

import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays, Users, Package, Receipt, CreditCard, Check, Clock, AlertTriangle } from 'lucide-react';
import type { Boat } from '@/types';
import { useBookingState, type ExternalServiceLine } from '@/hooks/useBookingState';
import type { RentalHoursSettings } from '@/lib/booking-utils';
import { bookableCapacity } from '@/lib/capacity';
import { stepsFor } from '@/lib/booking-wizard/steps';

interface BookingRequest {
  guests: number;
  selectedExtras: string[];
  startTime: string;
  endTime: string;
  dayParts?: Record<string, 'full' | 'morning' | 'afternoon'>;
}

export interface BookingWizardSidebarStepsProps {
  boat: Boat;
  selectedStart: Date | null;
  selectedEnd: Date | null;
  startTime?: string;
  endTime?: string;
  onDateClick: () => void;
  onTimeChange?: (startTime: string, endTime: string) => void;
  initialGuests?: number;
  initialExtras?: string[];
  initialDayParts?: Record<string, 'full' | 'morning' | 'afternoon'>;
  onBookingRequest?: (data: BookingRequest) => void;
  rentalHours?: RentalHoursSettings;
  isDark?: boolean;
  externalServices?: ExternalServiceLine[];
  /** Nasconde lo step Date (date già scelte a monte, es. modale disponibilità→wizard). */
  hideDatesStep?: boolean;
}

const euro = (n: number) => `€${(Number(n) || 0).toLocaleString('it-IT', { maximumFractionDigits: 0 })}`;
const fmtDate = (d: Date | null) => (d ? d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }) : '—');

const STEP_ICON: Record<string, React.ElementType> = {
  dates: CalendarDays, datetimeHourly: Clock, guests: Users, extras: Package, review: Receipt, payment: CreditCard,
};

export default function BookingWizardSidebarSteps(props: BookingWizardSidebarStepsProps) {
  const { boat, selectedStart, selectedEnd, onDateClick, onBookingRequest, externalServices = [] } = props;

  const bs = useBookingState({
    boat,
    selectedStart,
    selectedEnd,
    propStartTime: props.startTime,
    propEndTime: props.endTime,
    initialGuests: props.initialGuests,
    initialExtras: props.initialExtras,
    initialDayParts: props.initialDayParts,
    rentalHours: props.rentalHours,
    externalServices,
    onTimeChange: props.onTimeChange,
  });

  const mode = (boat.rentalMode as 'daily' | 'weekly' | 'monthly' | 'hourly') || 'daily';
  const steps = useMemo(() => {
    const all = stepsFor('rental', mode);
    return props.hideDatesStep ? all.filter((s) => s.key !== 'dates' && s.key !== 'datetimeHourly') : all;
  }, [mode, props.hideDatesStep]);
  const [stepIdx, setStepIdx] = useState(0);
  const cur = steps[Math.min(stepIdx, steps.length - 1)];

  const capacity = bookableCapacity(boat as unknown as { max_guests?: number | null; max_capacity?: number | null });
  const total = bs.effectiveGrandTotal + bs.externalServicesTotal;

  // Validazione per-step.
  const stepValid = (key: string): boolean => {
    if (key === 'dates' || key === 'datetimeHourly') return bs.hasDates;
    if (key === 'guests') return bs.guests >= 1 && (!capacity || bs.guests <= capacity);
    return true;
  };
  const canNext = stepValid(cur.key);
  const isLast = stepIdx === steps.length - 1;

  const submit = () => {
    onBookingRequest?.({
      guests: bs.guests,
      selectedExtras: bs.selectedExtras,
      startTime: bs.startTime,
      endTime: bs.endTime,
      dayParts: bs.perDayMode ? bs.dayParts : undefined,
    });
  };

  const card = props.isDark ? 'bg-[#132843] border-[rgba(198,168,94,0.15)] text-white' : 'bg-white border-slate-200 text-slate-900';
  const sub = props.isDark ? 'text-slate-300' : 'text-slate-500';

  return (
    <div data-cid="booking-wizard-sidebar-steps" className={`rounded-2xl border shadow-sm p-5 sticky top-4 ${card}`}>
      {/* Step pills */}
      <div className="flex items-center gap-1.5 mb-4">
        {steps.map((s, i) => {
          const Icon = STEP_ICON[s.key] || CalendarDays;
          const active = i === stepIdx; const done = i < stepIdx;
          return (
            <button
              key={s.key}
              type="button"
              data-cid={`bw-pill-${s.key}`}
              onClick={() => i <= stepIdx && setStepIdx(i)}
              className={`flex items-center gap-1 px-2 py-1.5 rounded-xl text-[11px] font-bold transition-colors ${
                active ? 'bg-sky-600 text-white' : done ? 'bg-sky-50 text-sky-700' : 'bg-slate-100 text-slate-400'
              }`}
              title={s.labelFallback}
            >
              {done ? <Check size={12} /> : <Icon size={12} />}
            </button>
          );
        })}
      </div>
      <p className={`text-[10px] font-black uppercase tracking-[0.18em] mb-3 ${sub}`}>{stepIdx + 1}/{steps.length} · {cur.labelFallback}</p>

      {/* Body */}
      <div className="space-y-3 min-h-[160px]">
        {(cur.key === 'dates' || cur.key === 'datetimeHourly') && (
          <div className="space-y-3" data-cid="bw-step-dates">
            <button type="button" onClick={onDateClick} className="w-full flex items-center justify-between p-3 rounded-xl border border-slate-200 hover:border-sky-300">
              <span className="text-sm font-bold">{fmtDate(selectedStart)} → {fmtDate(selectedEnd)}</span>
              <CalendarDays size={16} className="text-sky-600" />
            </button>
            {bs.halfDayAvailable && (
              <div className="grid grid-cols-2 gap-2">
                <label className="text-xs font-bold">
                  Dalle
                  <select value={bs.startTime} onChange={(e) => bs.handleTimeChangeSafe('start', e.target.value)} className="w-full mt-1 p-2 border border-slate-200 rounded-lg text-sm">
                    {bs.startTimeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
                <label className="text-xs font-bold">
                  Alle
                  <select value={bs.endTime} onChange={(e) => bs.handleTimeChangeSafe('end', e.target.value)} className="w-full mt-1 p-2 border border-slate-200 rounded-lg text-sm">
                    {bs.endTimeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
              </div>
            )}
            {bs.slotWarning && <p className="text-[11px] text-amber-600 flex items-center gap-1"><AlertTriangle size={12} /> {bs.slotWarning}</p>}
            {!bs.hasDates && <p className={`text-xs ${sub}`}>Seleziona le date per continuare.</p>}
          </div>
        )}

        {cur.key === 'guests' && (
          <div data-cid="bw-step-guests" className="flex items-center justify-between p-3 rounded-xl border border-slate-200">
            <span className="text-sm font-bold">Ospiti</span>
            <div className="flex items-center gap-3">
              <button type="button" data-cid="bw-guests-minus" onClick={() => bs.setGuests(Math.max(1, bs.guests - 1))} className="w-8 h-8 rounded-full border border-slate-300 font-bold">−</button>
              <span data-cid="bw-guests-value" className="w-6 text-center font-bold">{bs.guests}</span>
              <button type="button" data-cid="bw-guests-plus" onClick={() => bs.setGuests(capacity ? Math.min(capacity, bs.guests + 1) : bs.guests + 1)} className="w-8 h-8 rounded-full border border-slate-300 font-bold">+</button>
            </div>
          </div>
        )}

        {cur.key === 'extras' && (
          <div data-cid="bw-step-extras" className="space-y-2">
            {(boat.extras || []).length === 0 && <p className={`text-xs ${sub}`}>Nessun extra disponibile.</p>}
            {(boat.extras || []).map((e) => {
              const on = bs.selectedExtras.includes(e.id);
              const mandatory = bs.mandatoryExtraIds.includes(e.id);
              return (
                <button key={e.id} type="button" disabled={mandatory} onClick={() => bs.toggleExtra(e.id)}
                  className={`w-full flex items-center justify-between p-2.5 rounded-lg border text-sm ${on ? 'border-sky-600 bg-sky-50' : 'border-slate-200'} ${mandatory ? 'opacity-70' : ''}`}>
                  <span className="font-semibold">{e.label}{mandatory && ' (incluso)'}</span>
                  <span>{euro(Number(e.price))}</span>
                </button>
              );
            })}
            {externalServices.length > 0 && (
              <div className="pt-2 border-t border-slate-100">
                <p className={`text-[10px] font-bold uppercase ${sub} mb-1`}>Servizi esterni</p>
                {externalServices.map((s) => (
                  <div key={s.id} className="flex justify-between text-sm"><span>{s.name}</span><span>{euro(s.total)}</span></div>
                ))}
              </div>
            )}
          </div>
        )}

        {cur.key === 'review' && (
          <div data-cid="bw-step-review" className="space-y-2 text-sm">
            <div className="flex justify-between"><span className={sub}>Date</span><span className="font-bold">{fmtDate(selectedStart)} → {fmtDate(selectedEnd)}</span></div>
            <div className="flex justify-between"><span className={sub}>Ospiti</span><span className="font-bold">{bs.guests}</span></div>
            <div className="flex justify-between"><span className={sub}>Base</span><span>{euro(bs.effectiveBaseTotal)}</span></div>
            {bs.effectiveExtrasTotal > 0 && <div className="flex justify-between"><span className={sub}>Extra</span><span>{euro(bs.effectiveExtrasTotal)}</span></div>}
            {bs.externalServicesTotal > 0 && <div className="flex justify-between"><span className={sub}>Servizi</span><span>{euro(bs.externalServicesTotal)}</span></div>}
            <div className="flex justify-between pt-2 border-t border-slate-100 text-base font-black"><span>Totale</span><span>{euro(total)}</span></div>
          </div>
        )}

        {cur.key === 'payment' && (
          <div data-cid="bw-step-payment" className="space-y-3">
            <div className="flex justify-between text-base font-black"><span>Totale</span><span>{euro(total)}</span></div>
            <p className={`text-xs ${sub}`}>Conferma per procedere con la richiesta e il pagamento.</p>
          </div>
        )}
      </div>

      {/* Footer nav */}
      <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t border-slate-100">
        <button type="button" data-cid="bw-nav-back" disabled={stepIdx === 0} onClick={() => setStepIdx((s) => Math.max(0, s - 1))}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-bold text-slate-500 hover:bg-slate-100 disabled:opacity-40">
          <ChevronLeft size={15} /> Indietro
        </button>
        {!isLast ? (
          <button type="button" data-cid="bw-nav-next" disabled={!canNext} onClick={() => setStepIdx((s) => Math.min(steps.length - 1, s + 1))}
            className="inline-flex items-center gap-1 px-5 py-2.5 rounded-full bg-sky-600 text-white text-sm font-bold hover:bg-sky-700 disabled:opacity-50">
            Avanti <ChevronRight size={15} />
          </button>
        ) : (
          <button type="button" data-cid="bw-nav-submit" disabled={!bs.hasDates} onClick={submit}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-sky-600 text-white text-sm font-bold hover:bg-sky-700 disabled:opacity-50">
            <Check size={15} /> Richiedi · {euro(total)}
          </button>
        )}
      </div>
    </div>
  );
}
